import React, { useEffect, useState, useCallback } from 'react';
// Reutilise les styles des cartes titulaires (prestataire-card* + lmm-*) deja definis pour la
// modale "Lancer un marche". Aucune duplication CSS : meme rendu que la modale page Commandes.
import '../LancerMarcheModal/LancerMarcheModal.css';
import ModalProfilMembre from '../../pages/Publications/components/DetailCandidature/ModalProfilMembre';
import type { ProfileData } from '../../services/profileService';

// Titulaire renvoye par GET /prestataire/annuaire (annuaire des contrats du lot/departement).
interface Titulaire {
  id: number | string;
  rang: number;
  numero?: string | null;
  entite: { id: number; nom: string; logo_url?: string | null; siret?: string | null };
  lot_technique?: { id: number; nom: string; code: string; image_url?: string | null };
  prestataire?: { nom: string; prenom: string; email: string; telephone: string; fonction: string } | null;
}

interface TitulairesLotCardsProps {
  // Identifiant du lot technique et numero de departement (memes valeurs que la modale Lancer un marche).
  lotTechnique?: number | string;
  departement?: number | string;
  // MS : titulaires rangs 1/2/3. BC : un seul titulaire (centre). Defaut MS.
  mode?: 'MS' | 'BC';
  // Mode BC : rang du titulaire a afficher (le "rang concerne" cote prestataire = commande.rang).
  // Defaut 1 pour ne pas casser un appel BC existant. Ignore en mode MS (qui affiche les 3 rangs).
  rang?: number;
  // Mode BC : entite de la COMMANDE (commande.id_entite). Quand elle est fournie, la carte est celle de
  // cette entite dans l'annuaire du lot, et `rang` ne sert plus qu'a la pastille. L'annuaire donne le
  // titulaire ACTUEL d'un rang, qui peut changer (inversion de rangs) ; la commande, elle, reste liee a
  // son titulaire : c'est lui qu'on montre.
  entiteId?: number | string | null;
  // Carte cliquable (ouvre la modale profil) : defaut true. false => vignette purement decorative
  // (sans clic), pour un contexte d'apercu (ex. creation de chiffrage cote prestataire).
  clickable?: boolean;
}

const formatPhone = (phone?: string): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  let national = digits;
  if (digits.startsWith('33') && digits.length === 11) national = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) national = digits.slice(1);
  else return phone;
  return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
};

// Cartes des titulaires d'un lot (lot technique x departement), telles qu'affichees dans la modale
// "Lancer un marche subsequent". Lecture seule, autonome (fait son propre fetch).
const TitulairesLotCards: React.FC<TitulairesLotCardsProps> = ({ lotTechnique, departement, mode = 'MS', rang = 1, entiteId, clickable = true }) => {
  const parEntite = mode === 'BC' && entiteId !== undefined && entiteId !== null;
  const [titulaires, setTitulaires] = useState<Titulaire[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lotTechnique || !departement) {
      setTitulaires([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setTitulaires([]);

    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ departement: String(departement), lot: String(lotTechnique) });
    fetch(`${process.env.REACT_APP_API_URL}/prestataire/annuaire?${params}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((r) => r.json())
      .then((result: { data?: Titulaire[] }) => {
        if (cancelled) return;
        const contacts: Titulaire[] = result?.data ?? [];
        if (mode === 'BC') {
          // BC : une seule carte. Par entite de la commande quand on la connait, sinon par rang.
          const cible = parEntite
            ? contacts.find((c) => String(c.entite?.id) === String(entiteId))
            : contacts.find((c) => c.rang === rang);
          setTitulaires(cible ? [cible] : []);
        } else {
          const three = ([1, 2, 3].map((r) => contacts.find((c) => c.rang === r) ?? null).filter(Boolean)) as Titulaire[];
          setTitulaires(three);
        }
      })
      .catch(() => {
        if (!cancelled) setTitulaires([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lotTechnique, departement, mode, rang, entiteId, parEntite]);

  if (!lotTechnique || !departement) return null;

  // Modale profil du titulaire (meme pattern que l'onglet Analyse / OperationMarche) : clic sur une
  // carte -> fetch du profil de l'entite + ouverture de la modale annuaire en lecture seule.
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileContact, setProfileContact] = useState<
    { nom: string; prenom: string; email: string; telephone: string; fonction: string; contratNumero?: string | null } | null
  >(null);

  const openProfil = useCallback((t: Titulaire) => {
    const entiteId = t.entite?.id;
    if (entiteId === undefined || entiteId === null) return;
    setProfileContact(
      t.prestataire
        ? {
            nom: t.prestataire.nom || '',
            prenom: t.prestataire.prenom || '',
            email: t.prestataire.email || '',
            telephone: t.prestataire.telephone || '',
            fonction: t.prestataire.fonction || '',
            contratNumero: t.numero ?? null,
          }
        : null,
    );
    setProfile(null);
    setProfileError(null);
    setProfileLoading(true);
    setShowProfileModal(true);
    fetch(`${process.env.REACT_APP_API_URL}/annuaire/entites/${entiteId}/profil`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d: { data?: ProfileData } & Partial<ProfileData>) => setProfile((d?.data ?? d) as ProfileData))
      .catch((e: Error) => setProfileError(e.message || 'Impossible de charger le profil.'))
      .finally(() => setProfileLoading(false));
  }, []);

  const renderCard = (t: Titulaire) => (
    <div
      key={String(t.id)}
      className="prestataire-card prestataire-card--readonly lmm-titulaire-card"
      // clickable (defaut) : la carte ouvre la modale profil (pointerEvents auto override le
      // `.lmm-titulaire-card { pointer-events: none }` de la modale Lancer). clickable=false : vignette
      // purement decorative, aucun clic.
      style={clickable ? { width: '210px', cursor: 'pointer', pointerEvents: 'auto' } : { width: '210px' }}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? 'Voir le profil' : undefined}
      onClick={clickable ? () => openProfil(t) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openProfil(t);
              }
            }
          : undefined
      }
    >
      <div className="prestataire-card-inner">
        {/* Pastille "Titulaire rang X" masquee en marche subsequent (MS) ; conservee en BC. Par entite,
            le rang affiche est celui de la COMMANDE (prop), pas celui que l'annuaire lui donne aujourd'hui. */}
        {mode !== 'MS' && <span className="prestataire-card-type">Titulaire rang {parEntite ? rang : t.rang}</span>}
        <div className="prestataire-card-header">
          {t.lot_technique?.image_url ? (
            <img src={t.lot_technique.image_url} alt={t.lot_technique.nom} className="prestataire-card-banner-img" />
          ) : (
            <div className="prestataire-card-banner-placeholder" style={{ backgroundColor: '#f97316' }} />
          )}
        </div>
        <div className="prestataire-card-logo-wrapper">
          {t.entite?.logo_url ? (
            <img src={t.entite.logo_url} alt={t.entite.nom} className="prestataire-card-logo-img" />
          ) : (
            <span className="prestataire-card-logo-text">LOGO</span>
          )}
        </div>
        <div className="prestataire-card-body">
          <p className="prestataire-card-nom">{t.entite?.nom?.toUpperCase() || '-'}</p>
          {t.entite?.siret && (
            <p className="lmm-card-info"><span className="lmm-card-info-label">SIRET</span>{t.entite.siret}</p>
          )}
          {t.numero && (
            <p className="lmm-card-info"><span className="lmm-card-info-label">Contrat</span>n° {t.numero}</p>
          )}
          {t.prestataire && (t.prestataire.prenom || t.prestataire.nom) && (
            <p className="lmm-card-info lmm-card-info--contact">
              {t.prestataire.prenom} {t.prestataire.nom}
              {t.prestataire.fonction ? ` – ${t.prestataire.fonction}` : ''}
            </p>
          )}
          {t.prestataire?.email && (
            <p className="lmm-card-info">{t.prestataire.email}</p>
          )}
          {t.prestataire?.telephone && (
            <p className="lmm-card-info">{formatPhone(t.prestataire.telephone)}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="lmm-titulaire-section">
        {loading && <div className="lmm-titulaire-loading">Recherche en cours…</div>}
        {!loading && titulaires.length === 0 && (
          <div className="lmm-titulaire-infructueux">
            {parEntite
              ? "Le titulaire de cette commande n'apparaît plus dans l'annuaire du lot."
              : "Désolé, ce lot a été infructueux. Nous n'avons pas de titulaire à proposer pour l'instant."}
          </div>
        )}
        {!loading && titulaires.length > 0 && (
          <div className="lmm-titulaire-row">
            {mode === 'BC' ? (
              <>
                <div />
                {renderCard(titulaires[0])}
                <div />
              </>
            ) : (
              titulaires.map((t) => renderCard(t))
            )}
          </div>
        )}
      </div>

      <ModalProfilMembre
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setProfile(null);
          setProfileError(null);
        }}
        profile={profile}
        isLoading={profileLoading}
        error={profileError}
        contactInfo={profileContact}
        initialTab={0}
        showCompletion={false}
      />
    </>
  );
};

export default TitulairesLotCards;
